import { OrderStatusCode, OrderTraceKey } from '@/console/api/types'
import { ORDER_SEARCH_LIMITS, ORDER_STATUS_GROUPS, OrderSearchForm } from '@/console/tabs/fopt/orderSearch'

/**
 * LOGISPRD-13034 — 새로고침해도 콘솔 조회 조건이 남게 `sessionStorage` 에 넣는다.
 *
 * `localStorage` 가 아니라 `sessionStorage` 를 쓴다 — 콘솔 로그인 세션 자체가 `sessionStorage`
 * (`ApiStorage`)라 탭 단위가 이 화면의 기본 수명이다. 새 탭은 어차피 비로그인이고, "새로고침해도
 * 기억"이라는 요청은 같은 탭 기준이면 충분하다.
 *
 * 저장하는 건 조회 폼 입력값(`OrderSearchForm` + limit, Trace 키)뿐이다. 조회 결과·선택 행·Trace
 * 결과는 저장하지 않는다 — 이 화면은 진단 도구라 옛 응답을 되살리면 안 된다(`queryClient.ts` 의
 * "항상 서버에서 새로 받는다" 원칙과 같다).
 *
 * 읽기는 방어적이다: 저장된 값이 깨졌거나(JSON 파싱 실패), 필드가 없거나, 타입이 안 맞거나,
 * `orderStatuses` 에 모르는 코드가 섞여 있으면 `null` 을 반환하고 저장소 값을 지운다 — 사용자가
 * 옛 버전 값을 들고 있어도 화면이 깨지면 안 된다.
 */
export const ORDER_SEARCH_FORM_STORAGE_KEY = 'moms-console.order-search.v1'
export const TRACE_KEY_STORAGE_KEY = 'moms-console.order-trace.v1'

const KNOWN_STATUS_CODES = new Set<string>(ORDER_STATUS_GROUPS.flatMap((g) => g.codes))

export type StoredOrderSearchForm = { form: OrderSearchForm; limit: number }

const isString = (v: unknown): v is string => typeof v === 'string'

const readJson = (key: string): unknown => {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const clear = (key: string): void => {
  try {
    sessionStorage.removeItem(key)
  } catch {
    /* 사파리 프라이빗 모드 등 — 지우지 못해도 화면은 돌아야 한다 */
  }
}

const writeJson = (key: string, value: unknown): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* 사파리 프라이빗 모드 등 — 저장 실패해도 화면은 돌아야 한다 */
  }
}

/** `OrderSearchForm` 의 문자열 필드만 검증한다 — 나머지(`orderStatuses`)는 별도로 본다. */
const STRING_FIELDS: (keyof Omit<OrderSearchForm, 'orderStatuses'>)[] = [
  'outboundCutSeqId',
  'orderNumber',
  'refNo1',
  'orderOptionNumber',
  'collectedDateFrom',
  'collectedDateTo',
]

const parseOrderSearchForm = (value: unknown): OrderSearchForm | null => {
  if (!value || typeof value !== 'object') return null
  const v = value as Record<string, unknown>

  if (!STRING_FIELDS.every((field) => isString(v[field]))) return null
  if (!Array.isArray(v.orderStatuses)) return null
  if (!v.orderStatuses.every((code) => typeof code === 'string' && KNOWN_STATUS_CODES.has(code))) return null

  return {
    outboundCutSeqId: v.outboundCutSeqId as string,
    orderNumber: v.orderNumber as string,
    refNo1: v.refNo1 as string,
    orderOptionNumber: v.orderOptionNumber as string,
    orderStatuses: v.orderStatuses as OrderStatusCode[],
    collectedDateFrom: v.collectedDateFrom as string,
    collectedDateTo: v.collectedDateTo as string,
  }
}

export const loadOrderSearchForm = (): StoredOrderSearchForm | null => {
  const raw = readJson(ORDER_SEARCH_FORM_STORAGE_KEY)
  if (!raw || typeof raw !== 'object') {
    clear(ORDER_SEARCH_FORM_STORAGE_KEY)
    return null
  }

  const v = raw as Record<string, unknown>
  const form = parseOrderSearchForm(v.form)
  const limit = v.limit

  if (!form || typeof limit !== 'number' || !(ORDER_SEARCH_LIMITS as readonly number[]).includes(limit)) {
    clear(ORDER_SEARCH_FORM_STORAGE_KEY)
    return null
  }

  return { form, limit }
}

export const saveOrderSearchForm = (value: StoredOrderSearchForm): void => {
  writeJson(ORDER_SEARCH_FORM_STORAGE_KEY, value)
}

export const clearOrderSearchForm = (): void => {
  clear(ORDER_SEARCH_FORM_STORAGE_KEY)
}

const parseTraceKey = (value: unknown): OrderTraceKey | null => {
  if (!value || typeof value !== 'object') return null
  const v = value as Record<string, unknown>
  if (!isString(v.refNo1) || !isString(v.orderOptionNumber)) return null
  return { refNo1: v.refNo1, orderOptionNumber: v.orderOptionNumber }
}

export const loadTraceKey = (): OrderTraceKey | null => {
  const raw = readJson(TRACE_KEY_STORAGE_KEY)
  const key = parseTraceKey(raw)
  if (!key) {
    clear(TRACE_KEY_STORAGE_KEY)
    return null
  }
  return key
}

export const saveTraceKey = (key: OrderTraceKey): void => {
  writeJson(TRACE_KEY_STORAGE_KEY, key)
}
